import { z } from 'zod';
/**
 * Schemas for organization SCIM provisioning (KIR-4693).
 *
 * A SCIM credential is a **Descope Access Key** carrying the configured SCIM
 * role, scoped to the org's tenant. The customer's IdP presents it as the SCIM
 * bearer token (`ProjectID:AccessKey`) to provision/deprovision users into the
 * tenant.
 *
 * Modeled like the SIEM / SSO integrations: **one SCIM connection per org**,
 * stored in the shared `settings` table (`setting_type = 'scim'`) — not a table
 * of its own. Generating again replaces the connection (rotate-by-replace). The
 * stored config is metadata only (the Descope key id + display fields); the
 * plaintext bearer is returned exactly once, by `create`, and never stored.
 *
 * The SCIM base URL is a single project-level value (not per-tenant); it is
 * surfaced here so the Settings UI can show the customer both values together.
 */
export const scimCredentialSchema = z.object({
    /** Descope Access Key id — the source of truth this metadata mirrors. */
    descopeAccessKeyId: z.string(),
    /** First 10 characters of the Descope access-key id. Display only. */
    keyPrefix: z.string(),
    name: z.string().nullable(),
    createdAt: z.coerce.date(),
});
/**
 * The SCIM Provisioning view: the (shared, project-level) SCIM base URL the
 * customer's IdP points at, plus this org's single active credential (or null
 * when SCIM has not been connected yet).
 */
export const scimProvisioningConfigSchema = z.object({
    /** Project-level SCIM 2.0 base URL to enter in the IdP. Not secret. */
    scimUrl: z.string(),
    /** The org's active SCIM credential, or null when not connected. */
    credential: scimCredentialSchema.nullable(),
});
export const createScimCredentialRequestSchema = z.object({
    name: z.string().trim().min(1).max(100),
});
/**
 * The credential metadata plus the one-time secret bearer token and the SCIM URL
 * — the exact pair the customer's IdP needs. `bearerToken` is present on this
 * response and nowhere else; it is never stored and never re-readable.
 */
export const createScimCredentialResponseSchema = scimCredentialSchema.extend({
    /** `ProjectID:AccessKey` — the SCIM bearer token. Shown exactly once. */
    bearerToken: z.string(),
    /** The SCIM base URL to pair with the token. */
    scimUrl: z.string(),
});
export const disconnectScimProvisioningResponseSchema = z.object({
    success: z.boolean(),
});
//# sourceMappingURL=scim-provisioning.js.map